import type { UIRouterGlobals } from "@uirouter/angularjs";
import type { IScope } from "angular";
import { cloneDeep } from "lodash";
import { finalize } from "rxjs/operators";

import type {
    IConfigurationGroup,
    IFunctionality,
    IFunctionalityInsert,
    IResponseWithLoading,
    IViewCoverage,
} from "App/Modules/Models";
import { ConfigurationGroupType, FunctionalityType } from "App/Modules/Models";
import type { ISiteFunctionalitiesRepository, ISitesRepository } from "App/Modules/Repositories";
import type { IDefaultNamingService, IFormContainerResolve } from "App/Modules/Services";
import { extendWithLoadingAndFail } from "App/Modules/Services/ObservableExtensions";

export default class AddFunctionalityComponentController {
    public static $inject: string[] = [
        "$scope",
        "$uiRouterGlobals",
        "mmSitesRepository",
        "mmSiteFunctionalitiesRepository",
        "irDefaultNamingService",
    ];

    public originalFunctionality: IFunctionalityInsert;

    public functionality: IFunctionalityInsert;

    public functionalityTypes: typeof FunctionalityType;

    public configurationGroupType: ConfigurationGroupType;

    public configurationGroups: IResponseWithLoading<IConfigurationGroup[]>;

    public configurationGroup: IConfigurationGroup;

    public siteId: string;

    public typeName: string;

    public resolve: IFormContainerResolve<IFunctionalityInsert, IViewCoverage, IFunctionality>;

    public addResponse: IResponseWithLoading<IFunctionality>;

    constructor(
        private readonly $scope: IScope,
        private readonly $uiRouterGlobals: UIRouterGlobals,
        private readonly sitesRepository: ISitesRepository,
        private readonly siteFunctionalitiesRepository: ISiteFunctionalitiesRepository,
        private readonly defaultNamingService: IDefaultNamingService,
    ) {}

    public $onInit() {
        this.originalFunctionality = this.resolve.formData;
        this.functionality = cloneDeep(this.originalFunctionality);
        this.functionalityTypes = FunctionalityType;

        this.typeName = this.functionality.type as any;

        this.siteId = this.$uiRouterGlobals.params.siteId;

        if (
            FunctionalityType[this.functionality.type as any as keyof typeof FunctionalityType] ===
            FunctionalityType.MotionDetection
        ) {
            return;
        }

        switch (FunctionalityType[this.functionality.type as any as keyof typeof FunctionalityType]) {
            case FunctionalityType.Care:
                this.configurationGroupType = ConfigurationGroupType.Care;
                break;
            case FunctionalityType.FlameDetection:
                this.configurationGroupType = ConfigurationGroupType.Detection;
                break;
            case FunctionalityType.Recording:
                this.configurationGroupType = ConfigurationGroupType.Recording;
                break;
            case FunctionalityType.Monitoring:
                this.configurationGroupType = ConfigurationGroupType.Monitoring;
                break;
            default:
                throw new Error("Functionality type not supported.");
        }

        extendWithLoadingAndFail(this.sitesRepository.getConfigurationGroups(this.siteId, this.configurationGroupType))
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (response: IResponseWithLoading<IConfigurationGroup[]>) => {
                    if (!response.isLoading && response.data) {
                        this.configurationGroups = response;

                        this.configurationGroups.data.forEach((cg) =>
                            this.defaultNamingService.nameConfigurationGroup(cg),
                        );

                        if (response.data.length === 1) {
                            this.configurationGroup = response.data[0];
                        }
                    }
                },
                (error) => {
                    this.configurationGroups = error;
                },
            );
    }

    public cancel() {
        if (this.resolve.options.onCancel) {
            this.resolve.options.onCancel();
        }
    }

    public addFunctionality() {
        if (this.configurationGroup !== undefined) {
            this.functionality.configurationGroupId = this.configurationGroup.id;
        }

        extendWithLoadingAndFail(this.siteFunctionalitiesRepository.addFunctionality(this.siteId, this.functionality))
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (response: IResponseWithLoading<IFunctionality>) => {
                    this.addResponse = response;
                    if (!response.isLoading && response.data) {
                        this.resolve.options.onOk(response.data);
                    }
                },
                (error) => {
                    this.addResponse = error;
                },
            );
    }
}
